/* eslint-disable */
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Box, Typography } from '@mui/material';
import dateIcon from 'Images/date_icon.svg';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const MONTHS_ABBR_FR = ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Août','Sept','Oct','Nov','Déc'];
const DAYS_FR   = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];
const EARLIEST_DATA_YEAR = 2010; // ops_order's earliest real record (verified via DB: MIN(date) = 2010-01-31)

// ─── Helpers ──────────────────────────────────────────────────────────────────

function dayTime(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function formatDate(date) {
  if (!date) return '';
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${d}-${m}-${date.getFullYear()}`;
}

// ─── Segmented date-mask helpers (format: DD-MM-YYYY, always a fixed 10-char skeleton) ──
//
// Each segment's *raw* value (state) is a plain digit string of 0..len characters — the
// real digits typed so far, nothing else. The rendered/input value is always the full
// 10-char skeleton: each segment padded out to its fixed width with a placeholder
// character, dashes always at position 2 and 5. This is what makes an empty segment a
// stable, selectable range (never "nothing there") and keeps the dashes structural —
// they can never be deleted or collapsed away, because they're never derived from
// whether a segment happens to be empty.

const SEGMENT_DEFS = [
  { key: 'day', start: 0, len: 2, placeholder: 'J' },
  { key: 'month', start: 3, len: 2, placeholder: 'M' },
  { key: 'year', start: 6, len: 4, placeholder: 'A' },
];

function padSegment(str, len, placeholder) {
  return (str + placeholder.repeat(len)).slice(0, len);
}

function buildMaskedValue(day, month, year) {
  const d = padSegment(day, 2, 'J');
  const m = padSegment(month, 2, 'M');
  const y = padSegment(year, 4, 'A');
  return `${d}-${m}-${y}`;
}

function segmentBoundsAt(pos) {
  if (pos < 3) return SEGMENT_DEFS[0];
  if (pos < 6) return SEGMENT_DEFS[1];
  return SEGMENT_DEFS[2];
}

// Every segment whose character range overlaps [start, end) — a selection can span more than one.
function segmentsInRange(start, end) {
  if (end <= start) return [segmentBoundsAt(start)];
  return SEGMENT_DEFS.filter((s) => start < s.start + s.len && end > s.start);
}

function buildCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  // Convert Sunday-based to Monday-based (Mon=0 ... Sun=6)
  const offset = (firstDay + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return cells;
}

// ─── Mini Calendar ────────────────────────────────────────────────────────────

function MiniCalendar({ year, month, startDate, endDate, onDayClick, onPrevMonth, onNextMonth, onNavigate, maxFutureYears = 0 }) {
  const [view, setView] = useState('days'); // 'days' | 'months' | 'years'
  const selectedYearRef = useRef(null);

  useEffect(() => {
    if (view === 'years' && selectedYearRef.current) {
      selectedYearRef.current.scrollIntoView({ block: 'center' });
    }
  }, [view]);

  const openMonths = () => setView('months');
  const openYears = () => setView('years');
  const handlePrevYear = () => onNavigate(year - 1, month);
  const handleNextYear = () => onNavigate(year + 1, month);
  const handleMonthPick = (monthIndex) => {
    onNavigate(year, monthIndex);
    setView('days');
  };
  const handleYearPick = (pickedYear) => {
    onNavigate(pickedYear, month);
    setView('days');
  };

  if (view === 'months') {
    const monthsGrid = [];
    for (let r = 0; r < 4; r += 1) monthsGrid.push(MONTHS_ABBR_FR.slice(r * 3, r * 3 + 3));
    return (
      <Box sx={{ width: '304px', p: '12px 16px 8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '4px', mb: 1 }}>
          <Box onClick={handlePrevYear} sx={{ cursor: 'pointer', color: '#3D3D3D', display: 'flex', '&:hover': { color: '#027BC6' } }}>
            <ChevronLeftIcon fontSize="small" />
          </Box>
          <Typography onClick={openYears} sx={{ fontWeight: 700, fontSize: 15, color: '#3D3D3D', cursor: 'pointer', userSelect: 'none', '&:hover': { color: '#027BC6' } }}>
            {year}
          </Typography>
          <Box onClick={handleNextYear} sx={{ cursor: 'pointer', color: '#3D3D3D', display: 'flex', '&:hover': { color: '#027BC6' } }}>
            <ChevronRightIcon fontSize="small" />
          </Box>
        </Box>
        <Box sx={{ py: 1 }}>
          {monthsGrid.map((row, ri) => (
            <Box key={ri} sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', mb: 1 }}>
              {row.map((label, ci) => {
                const monthIndex = ri * 3 + ci;
                const isSelected = monthIndex === month;
                return (
                  <Box
                    key={label}
                    onClick={() => handleMonthPick(monthIndex)}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      height: 44, mx: '4px', borderRadius: '8px', cursor: 'pointer',
                      backgroundColor: isSelected ? '#027BC6' : 'transparent',
                      '&:hover': { backgroundColor: isSelected ? '#027BC6' : '#F0F0F0' },
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: isSelected ? '#FFFFFF' : '#3D3D3D', userSelect: 'none' }}>
                      {label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (view === 'years') {
    const startYear = EARLIEST_DATA_YEAR;
    const endYear = year + maxFutureYears;
    const yearsCount = Math.max(0, endYear - startYear + 1);
    const years = Array.from({ length: yearsCount }, (_, i) => startYear + i);
    return (
      <Box sx={{ width: '304px', p: '12px 16px 8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '4px', mb: 1 }}>
          <Box onClick={() => setView('days')} sx={{ cursor: 'pointer', color: '#3D3D3D', display: 'flex', '&:hover': { color: '#027BC6' } }}>
            <ChevronLeftIcon fontSize="small" />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#3D3D3D', userSelect: 'none' }}>
            Année
          </Typography>
        </Box>
        <Box sx={{
          maxHeight: '260px', overflowY: 'auto', py: 1,
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px',
          scrollbarWidth: 'none', // Firefox
          '&::-webkit-scrollbar': { display: 'none' }, // Chrome/Safari/Edge
        }}
        >
          {years.map((y) => {
            const isSelected = y === year;
            return (
              <Box
                key={y}
                ref={isSelected ? selectedYearRef : null}
                onClick={() => handleYearPick(y)}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  height: 44, borderRadius: '8px', cursor: 'pointer',
                  backgroundColor: isSelected ? '#027BC6' : 'transparent',
                  '&:hover': { backgroundColor: isSelected ? '#027BC6' : '#F0F0F0' },
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: isSelected ? '#FFFFFF' : '#3D3D3D', userSelect: 'none' }}>
                  {y}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  const cells = buildCalendarDays(year, month);

  // Returns { stripBg, circleBg, color, shadow }
  // stripBg  → full-width background (the gray range strip)
  // circleBg → inner circle background (blue for start/end)
  const getDayInfo = (day) => {
    if (!day) return { stripBg: 'transparent', circleBg: 'transparent', color: '#000000', shadow: 'none' };

    const t  = dayTime(new Date(year, month, day));
    const st = startDate ? dayTime(startDate) : null;
    const et = endDate   ? dayTime(endDate)   : null;

    const isStart  = st !== null && t === st;
    const isEnd    = et !== null && t === et;
    const isSingle = isStart && isEnd;
    const inRange  = st !== null && et !== null && t > st && t < et;


    if (isSingle) {
      return { stripBg: 'transparent', stripLeft: '0', stripWidth: '100%', circleBg: '#027BC6', color: '#FFFFFF', shadow: '0px 4px 10px rgba(106,120,248,0.1)' };
    }
    if (isStart) {
      const hasEnd = et !== null;
      return { stripBg: hasEnd ? '#CFCFCF' : 'transparent', stripLeft: '50%', stripWidth: '50%', circleBg: '#027BC6', color: '#FFFFFF', shadow: '0px 4px 10px rgba(106,120,248,0.1)' };
    }
    if (isEnd) {
      return { stripBg: '#CFCFCF', stripLeft: '0', stripWidth: '50%', circleBg: '#027BC6', color: '#FFFFFF', shadow: '0px 4px 10px rgba(106,120,248,0.1)' };
    }
    if (inRange) {
      return { stripBg: '#CFCFCF', stripLeft: '0', stripWidth: '100%', circleBg: 'transparent', color: '#3D3D3D', shadow: 'none' };
    }
    return { stripBg: 'transparent', stripLeft: '0', stripWidth: '100%', circleBg: 'transparent', color: '#000000', shadow: 'none' };
  };

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  return (
    <Box sx={{ width: '304px', p: '12px 16px 8px' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '4px', mb: 1 }}>
        <Box onClick={onPrevMonth} sx={{ cursor: 'pointer', color: '#3D3D3D', display: 'flex', '&:hover': { color: '#027BC6' } }}>
          <ChevronLeftIcon fontSize="small" />
        </Box>
        <Typography component="span" onClick={openMonths} sx={{ fontWeight: 700, fontSize: 15, color: '#3D3D3D', cursor: 'pointer', userSelect: 'none', '&:hover': { color: '#027BC6' } }}>
          {MONTHS_FR[month]}
        </Typography>
        <Typography component="span" onClick={openYears} sx={{ fontWeight: 700, fontSize: 15, color: '#3D3D3D', cursor: 'pointer', userSelect: 'none', '&:hover': { color: '#027BC6' } }}>
          {year}
        </Typography>
        <Box onClick={onNextMonth} sx={{ cursor: 'pointer', color: '#3D3D3D', display: 'flex', '&:hover': { color: '#027BC6' } }}>
          <ChevronRightIcon fontSize="small" />
        </Box>
      </Box>

      {/* Day headers */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
        {DAYS_FR.map((d) => (
          <Typography key={d} align="center" sx={{ fontSize: 11, color: '#888', fontWeight: 600, py: 0.3 }}>
            {d}
          </Typography>
        ))}
      </Box>

      {/* Day cells */}
      {weeks.map((week, wi) => (
        <Box key={wi} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {week.map((day, di) => {
            const s = getDayInfo(day);

            // Determine row boundaries for strip border-radius
            const isFirstInRow = di === 0 || week.slice(0, di).every(d => d === null);
            const isLastInRow  = di === 6 || week.slice(di + 1).every(d => d === null);

            const st = startDate ? dayTime(startDate) : null;
            const et = endDate   ? dayTime(endDate)   : null;
            const t  = day ? dayTime(new Date(year, month, day)) : null;
            const isStart = t !== null && st !== null && t === st;
            const isEnd   = t !== null && et !== null && t === et;

            // Round left edge at start of row (unless it's the start day whose gradient already handles the left)
            // Round right edge at end of row (unless it's the end day whose gradient already handles the right)
            const leftR  = isFirstInRow && !isStart ? '8px' : '0';
            const rightR = isLastInRow  && !isEnd   ? '8px' : '0';

            return (
              <Box
                key={di}
                onClick={() => day && onDayClick(new Date(year, month, day))}
                sx={{
                  position: 'relative',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  height: 36,
                  cursor: day ? 'pointer' : 'default',
                }}
              >
                {/* Gray range strip — same height as the circle, rounded at row edges */}
                {s.stripBg !== 'transparent' && (
                  <Box sx={{
                    position: 'absolute',
                    top: '50%', transform: 'translateY(-50%)',
                    left: s.stripLeft, width: s.stripWidth, height: 32,
                    background: s.stripBg,
                    opacity: 0.31,
                    // Start day: round the left (inner) edge; End day: round the right (inner) edge
                    // In-range: round at row boundaries
                    borderTopLeftRadius:    isStart ? '8px' : leftR,
                    borderBottomLeftRadius: isStart ? '8px' : leftR,
                    borderTopRightRadius:   isEnd   ? '8px' : rightR,
                    borderBottomRightRadius:isEnd   ? '8px' : rightR,
                    pointerEvents: 'none',
                  }} />
                )}

                {/* Day circle */}
                {day && (
                  <Box
                    sx={{
                      position: 'relative',
                      width: 32, height: 32,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: s.circleBg,
                      borderRadius: '8px',
                      boxShadow: s.shadow,
                      flexShrink: 0,
                      '&:hover': s.circleBg === 'transparent'
                        ? { backgroundColor: '#F0F0F0', borderRadius: '8px' }
                        : {},
                    }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: s.color, userSelect: 'none', lineHeight: 1 }}>
                      {day}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}

// ─── SingleDatePicker ─────────────────────────────────────────────────────────

export function SingleDatePicker({ value, onApply, placeholder = 'Sélectionner une date', floatingLabel = false, maxFutureYears = 0 }) {
  const today = new Date();
  const [showCalendar, setShowCalendar] = useState(false);
  const [selected, setSelected]         = useState(null);
  const [viewYear, setViewYear]         = useState(today.getFullYear());
  const [viewMonth, setViewMonth]       = useState(today.getMonth());
  const [editingText, setEditingText]   = useState(false);
  const [daySeg, setDaySeg]             = useState('');
  const [monthSeg, setMonthSeg]         = useState('');
  const [yearSeg, setYearSeg]           = useState('');
  const isOpeningRef = useRef(false);
  const wrapperRef   = useRef(null);
  const selectedRef  = useRef(null);
  const textInputRef = useRef(null);

  useEffect(() => {
    if (!value) { setSelected(null); selectedRef.current = null; }
  }, [value]);

  // Runs before the browser paints, so the input never shows its default
  // (unselected, caret-at-start) state for a frame before select() applies.
  useLayoutEffect(() => {
    if (editingText && textInputRef.current) {
      textInputRef.current.select();
    }
  }, [editingText]);

  const commitSelection = () => {
    if (selectedRef.current) onApply && onApply(selectedRef.current);
  };

  useEffect(() => {
    if (!showCalendar) return;
    const handleClickOutside = (e) => {
      if (isOpeningRef.current) return;
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowCalendar(false);
        commitSelection();
      }
    };
    const timeout = setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 0);
    return () => { clearTimeout(timeout); document.removeEventListener('mousedown', handleClickOutside); };
  }, [showCalendar]);

  const openCalendar = () => {
    isOpeningRef.current = true;
    setShowCalendar(true);
    setTimeout(() => { isOpeningRef.current = false; }, 100);
  };

  const handleIconClick = (e) => {
    e.stopPropagation();
    setEditingText(false);
    if (showCalendar) { setShowCalendar(false); commitSelection(); }
    else openCalendar();
  };

  const handleTextClick = (e) => {
    e.stopPropagation();
    if (showCalendar) return;
    if (value) {
      setDaySeg(String(value.getDate()).padStart(2, '0'));
      setMonthSeg(String(value.getMonth() + 1).padStart(2, '0'));
      setYearSeg(String(value.getFullYear()));
      setEditingText(true);
    } else {
      openCalendar();
    }
  };

  const segmentValues = () => ({ day: daySeg, month: monthSeg, year: yearSeg });
  const segmentSetters = { day: setDaySeg, month: setMonthSeg, year: setYearSeg };

  const selectSegmentAtCaret = (input) => {
    const { start, len } = segmentBoundsAt(input.selectionStart);
    input.setSelectionRange(start, start + len);
  };

  const handleInputClick = (e) => {
    e.stopPropagation();
    const input = e.target;
    setTimeout(() => selectSegmentAtCaret(input), 0);
  };

  const setCaretLater = (input, pos) => {
    setTimeout(() => input.setSelectionRange(pos, pos), 0);
  };

  const selectRangeLater = (input, start, end) => {
    setTimeout(() => input.setSelectionRange(start, end), 0);
  };

  const handleInputKeyDown = (e) => {
    const input = e.target;
    const { key } = e;

    if (key === 'Enter') { e.preventDefault(); input.blur(); return; }
    if (key === 'Escape') { e.preventDefault(); setEditingText(false); return; }
    if (key === 'ArrowLeft' || key === 'ArrowRight' || key === 'Tab') return; // let the browser move the caret, we resync selection on click only

    if (/^[0-9]$/.test(key)) {
      e.preventDefault();
      const pos = input.selectionStart;
      const end = input.selectionEnd;
      const hasSelection = end > pos;
      const values = segmentValues();

      const seg = hasSelection ? segmentsInRange(pos, end)[0] : segmentBoundsAt(pos);
      if (hasSelection) {
        segmentsInRange(pos, end).forEach((s) => segmentSetters[s.key](''));
      }
      const segStr = hasSelection ? '' : values[seg.key];
      const localPos = hasSelection ? 0 : Math.min(Math.max(pos - seg.start, 0), segStr.length);
      const newSegStr = (segStr.slice(0, localPos) + key + segStr.slice(localPos)).slice(0, seg.len);
      segmentSetters[seg.key](newSegStr);

      if (newSegStr.length >= seg.len && seg.key !== 'year') {
        // Auto-advance selects the next segment's full (always-present, placeholder-or-real) range,
        // so the next digit typed cleanly replaces it instead of inserting-and-truncating.
        const nextSeg = seg.key === 'day' ? SEGMENT_DEFS[1] : SEGMENT_DEFS[2];
        selectRangeLater(input, nextSeg.start, nextSeg.start + nextSeg.len);
      } else {
        setCaretLater(input, seg.start + newSegStr.length);
      }
      return;
    }

    if (key === 'Backspace' || key === 'Delete') {
      e.preventDefault();
      const pos = input.selectionStart;
      const end = input.selectionEnd;
      const values = segmentValues();

      if (end > pos) {
        const overlapped = segmentsInRange(pos, end);
        overlapped.forEach((s) => segmentSetters[s.key](''));
        setCaretLater(input, overlapped[0].start);
        return;
      }

      if (key === 'Delete') {
        const seg = segmentBoundsAt(pos);
        const segStr = values[seg.key];
        const localPos = Math.min(Math.max(pos - seg.start, 0), segStr.length);
        segmentSetters[seg.key](segStr.slice(0, localPos) + segStr.slice(localPos + 1));
        setCaretLater(input, pos);
        return;
      }

      let seg = segmentBoundsAt(pos);
      let localPos = pos - seg.start;
      if (localPos <= 0) {
        if (seg.key === 'month') seg = SEGMENT_DEFS[0];
        else if (seg.key === 'year') seg = SEGMENT_DEFS[1];
        else return;
        localPos = values[seg.key].length;
      }
      const segStr = values[seg.key];
      segmentSetters[seg.key](segStr.slice(0, localPos - 1) + segStr.slice(localPos));
      setCaretLater(input, seg.start + localPos - 1);
      return;
    }

    // Block every other printable character (including '-') — dashes are structural, never typed
    if (key.length === 1 && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
    }
  };

  const commitTextEdit = () => {
    setEditingText(false);
    if (daySeg.length === 2 && monthSeg.length === 2 && yearSeg.length === 4) {
      const d = Number(daySeg);
      const m = Number(monthSeg);
      const y = Number(yearSeg);
      const date = new Date(y, m - 1, d);
      if (date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d) {
        onApply && onApply(date);
      }
    }
  };

  const handleDayClick = (date) => {
    setSelected(date);
    selectedRef.current = date;
  };

  const handlePrevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const handleApply = (e) => {
    e.stopPropagation();
    setShowCalendar(false);
    if (selected) onApply && onApply(selected);
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    setShowCalendar(false);
  };

  return (
    <Box ref={wrapperRef} sx={{ flex: 1, position: 'relative' }}>
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          border: `1.5px solid ${(showCalendar || editingText || (floatingLabel && value)) ? '#007BC6' : '#DEE5EE'}`,
          borderRadius: '8px', px: '12px',
          height: floatingLabel ? '52px' : '45px', gap: '8px',
          backgroundColor: '#F8FAFC',
          flexDirection: floatingLabel && value ? 'row' : 'row',
        }}
      >
        <Box onClick={handleTextClick} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1, cursor: value ? 'text' : 'pointer' }}>
          {floatingLabel && value && !editingText && (
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#007BC6', lineHeight: 1, mb: '3px', fontFamily: "'Open Sans', sans-serif" }}>
              {placeholder}
            </Typography>
          )}
          {editingText ? (
            <input
              ref={textInputRef}
              value={buildMaskedValue(daySeg, monthSeg, yearSeg)}
              onChange={() => {}}
              onClick={handleInputClick}
              onBlur={commitTextEdit}
              onKeyDown={handleInputKeyDown}
              onPaste={(e) => e.preventDefault()}
              placeholder="JJ-MM-AAAA"
              style={{
                fontSize: '13px', fontWeight: 600, color: '#385576', fontFamily: "'Open Sans', sans-serif",
                border: 'none', outline: 'none', background: 'transparent', padding: 0, width: '100%',
              }}
            />
          ) : (
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: value ? '#385576' : '#8397AE', userSelect: 'none', fontFamily: "'Open Sans', sans-serif" }}>
              {value ? formatDate(value) : placeholder}
            </Typography>
          )}
        </Box>
        <Box onClick={handleIconClick} sx={{ width: 16, height: 16, flexShrink: 0, cursor: 'pointer', backgroundColor: '#8397AE', WebkitMaskImage: `url(${dateIcon})`, maskImage: `url(${dateIcon})`, WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat', WebkitMaskSize: 'contain', maskSize: 'contain', WebkitMaskPosition: 'center', maskPosition: 'center' }} />
      </Box>

      {showCalendar && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute', zIndex: 1400,
            top: 'calc(100% + 6px)', left: 0,
            backgroundColor: '#FFFFFF',
            border: '1px solid #F7F7F7',
            boxShadow: '0px 12px 26px #0000001C',
            borderRadius: '12px', overflow: 'hidden',
          }}
        >
          <MiniCalendar
            year={viewYear}
            month={viewMonth}
            startDate={selected}
            endDate={selected}
            onDayClick={handleDayClick}
            onPrevMonth={handlePrevMonth}
            onNextMonth={handleNextMonth}
            onNavigate={(y, m) => { setViewYear(y); setViewMonth(m); }}
            maxFutureYears={maxFutureYears}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 2, pb: 1.5 }}>
            <Box component="button" onClick={handleCancel}
              sx={{ border: 'none', background: 'transparent', color: '#027BC6', fontSize: 13, fontWeight: 600, cursor: 'pointer', px: 1.5, py: 0.5, borderRadius: '8px', '&:hover': { background: '#E3F2FD' } }}>
              Annuler
            </Box>
            <Box component="button" onClick={handleApply}
              sx={{ border: 'none', background: '#007BC6', color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: 'pointer', px: 2, py: 0.5, borderRadius: '8px', '&:hover': { background: '#0263A0' } }}>
              Appliquer
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ─── DateRangePicker ──────────────────────────────────────────────────────────

export default function DateRangePicker({ value, onApply, placeholder = "Date début – Date fin", width = '304px', maxFutureYears = 0 }) {
  const today = new Date();
  const [showCalendar, setShowCalendar] = useState(false);
  const [startDate, setStartDate]       = useState(null);
  const [endDate, setEndDate]           = useState(null);
  const [viewYear, setViewYear]         = useState(today.getFullYear());
  const [viewMonth, setViewMonth]       = useState(today.getMonth());
  const isOpeningRef = useRef(false);
  const wrapperRef   = useRef(null);

  // Sync when reset from outside
  useEffect(() => {
    if (!value?.start && !value?.end) {
      setStartDate(null);
      setEndDate(null);
    }
  }, [value]);

  // Close on outside click
  useEffect(() => {
    if (!showCalendar) return;
    const handleClickOutside = (e) => {
      if (isOpeningRef.current) return;
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
    };
    const timeout = setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 0);
    return () => { clearTimeout(timeout); document.removeEventListener('mousedown', handleClickOutside); };
  }, [showCalendar]);

  const handleTriggerClick = (e) => {
    e.stopPropagation();
    if (showCalendar) {
      setShowCalendar(false);
    } else {
      isOpeningRef.current = true;
      setShowCalendar(true);
      setTimeout(() => { isOpeningRef.current = false; }, 100);
    }
  };

  const handleDayClick = (date) => {
    if (!startDate) {
      setStartDate(date);
    } else if (!endDate) {
      if (dayTime(date) >= dayTime(startDate)) {
        setEndDate(date);
      } else {
        setStartDate(date);
        setEndDate(null);
      }
    } else {
      setStartDate(date);
      setEndDate(null);
    }
  };

  const handlePrevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const handleApply = (e) => {
    e.stopPropagation();
    setShowCalendar(false);
    if (startDate && endDate) onApply && onApply(startDate, endDate);
    else if (startDate) onApply && onApply(startDate, startDate);
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    setShowCalendar(false);
  };


  const hasValue = value?.start;

  return (
    <Box ref={wrapperRef} sx={{ width, flexShrink: 0, position: 'relative' }}>

      {/* Trigger */}
      <Box
        onClick={handleTriggerClick}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          border: hasValue ? '1px solid #007BC6' : '1px solid #DEE5EE',
          borderRadius: '8px', px: '12px', height: '45px', gap: '8px',
          backgroundColor: '#F8FAFC', cursor: 'pointer',
        }}
      >
        <Typography sx={{ fontSize: '13px', lineHeight: '22px', fontWeight: 600, color: hasValue ? '#385576' : '#8397AE', userSelect: 'none', flex: 1, fontFamily: "'Open Sans', sans-serif", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {value?.start && value?.end
            ? `${formatDate(value.start)} – ${formatDate(value.end)}`
            : value?.start ? formatDate(value.start)
            : placeholder}
        </Typography>
        <Box sx={{ width: 16, height: 16, flexShrink: 0, backgroundColor: '#8397AE', WebkitMaskImage: `url(${dateIcon})`, maskImage: `url(${dateIcon})`, WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat', WebkitMaskSize: 'contain', maskSize: 'contain', WebkitMaskPosition: 'center', maskPosition: 'center' }} />
      </Box>

      {/* Dropdown */}
      {showCalendar && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute', zIndex: 1300,
            top: 'calc(100% + 6px)', left: 0,
            backgroundColor: '#FFFFFF',
            border: '1px solid #F7F7F7',
            boxShadow: '0px 12px 26px #0000001C',
            borderRadius: '12px', overflow: 'hidden',
          }}
        >
          <MiniCalendar
            year={viewYear}
            month={viewMonth}
            startDate={startDate}
            endDate={endDate}
            onDayClick={handleDayClick}
            onPrevMonth={handlePrevMonth}
            onNextMonth={handleNextMonth}
            onNavigate={(y, m) => { setViewYear(y); setViewMonth(m); }}
            maxFutureYears={maxFutureYears}
          />

          {/* Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 2, pb: 1.5 }}>

            <Box component="button" onClick={handleCancel}
              sx={{ border: 'none', background: 'transparent', color: '#027BC6', fontSize: 13, fontWeight: 600, cursor: 'pointer', px: 1.5, py: 0.5, borderRadius: '8px', '&:hover': { background: '#E3F2FD' } }}>
              Annuler
            </Box>
            <Box component="button" onClick={handleApply}
              sx={{ border: 'none', background: '#007BC6', color: '#FFFFFF', fontSize: 13, fontWeight: 600, cursor: 'pointer', px: 2, py: 0.5, borderRadius: '8px', boxShadow: '0px 4px 10px #6A78F819', '&:hover': { background: '#0263A0' } }}>
              Appliquer
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}